/**
 * redFlagChecklists.js — the three standalone red-flag checklists the spec calls for.
 *
 * These are review tools, not contracts: a founder holds a draft in one hand and walks the list.
 * Each item is phrased as something to look for, with the consequence stated plainly.
 */

export const RED_FLAG_CHECKLISTS = [
  {
    id: "co-packer",
    docId: "co-packer-agreement",
    title: "Co-Packer Agreement — Red Flag Checklist",
    icon: "Factory",
    accent: "amber",
    intro:
      "Walk this list against the draft before you sign. Anything you cannot answer from the document itself is the answer.",
    sections: [
      {
        title: "Ownership",
        items: [
          { flag: "The agreement is silent on who owns the formula.", consequence: "You may not be able to take your own product to another manufacturer." },
          { flag: "The co-packer owns, or gets a licence to, 'improvements' or 'modifications'.", consequence: "Every optimisation made during production quietly becomes theirs." },
          { flag: "Specifications are described as the co-packer's property or trade secret.", consequence: "You cannot hand a new manufacturer the document that defines your product." },
          { flag: "No obligation to return specifications, artwork or tooling on termination.", consequence: "Leaving costs you the ability to make the product." },
        ],
      },
      {
        title: "Money",
        items: [
          { flag: "An unlimited or undefined right to raise price.", consequence: "You cannot plan margin or hold retail pricing." },
          { flag: "No cap on excess and obsolete ingredients or packaging.", consequence: "A forecast miss becomes an open-ended invoice." },
          { flag: "A yield or variance allowance you have not modelled.", consequence: "You pay for product you never receive on every run." },
          { flag: "Forecasts described as binding commitments without a stated window.", consequence: "A planning document turns into a purchase obligation." },
          { flag: "Deposits that are non-refundable in every circumstance, including their failure.", consequence: "You fund a run that may never ship." },
        ],
      },
      {
        title: "Quality and risk",
        items: [
          { flag: "No signed specification sheet attached or referenced.", consequence: "'Correct product' is undefined when a run comes out wrong." },
          { flag: "Silence on recall cost allocation.", consequence: "You will be arguing about who pays in the middle of a recall." },
          { flag: "Liability capped below the cost of one production run.", consequence: "A defective batch costs you more than your remedy is worth." },
          { flag: "You are not named as an additional insured.", consequence: "Their coverage may do nothing for you." },
          { flag: "No allergen changeover or sanitation obligations where the facility runs allergens.", consequence: "A cross-contact event lands on your label and your brand." },
          { flag: "No obligation to maintain facility certifications during the term.", consequence: "A lapsed certification can pull you off a retailer's shelf." },
        ],
      },
      {
        title: "Getting out",
        items: [
          { flag: "A long initial term with no termination for convenience.", consequence: "You are locked in regardless of performance." },
          { flag: "Automatic renewal unless you give notice far in advance.", consequence: "You renew by forgetting." },
          { flag: "No transition assistance obligation.", consequence: "Moving production becomes a standing start." },
          { flag: "Exclusivity running against you with no volume or service commitment from them.", consequence: "You are restricted; they are not." },
        ],
      },
    ],
  },

  {
    id: "distributor",
    docId: "beverage-distributor-agreement",
    title: "Distributor Agreement — Red Flag Checklist",
    icon: "Truck",
    accent: "blue",
    intro:
      "The highest-stakes agreement most CPG founders sign. A bad one follows the brand into every future conversation, including an acquisition.",
    sections: [
      {
        title: "Exclusivity and territory",
        items: [
          { flag: "Exclusivity with no performance minimums.", consequence: "A territory can be frozen for years while producing almost no sales." },
          { flag: "Territory defined loosely, or larger than they can actually service.", consequence: "You give away regions they will never work." },
          { flag: "No carve-out for e-commerce, Amazon or your own DTC.", consequence: "You pay margin on sales you generate yourself." },
          { flag: "No carve-out for house accounts you already service.", consequence: "Existing business becomes theirs." },
          { flag: "No carve-out for military, government or foodservice where relevant.", consequence: "Whole channels get locked up by default." },
        ],
      },
      {
        title: "Performance",
        items: [
          { flag: "No minimum purchase or minimum sales requirement.", consequence: "Nothing obliges them to actually sell your product." },
          { flag: "Minimums with no consequence for missing them.", consequence: "A target without a remedy is a suggestion." },
          { flag: "No account coverage or call frequency obligations.", consequence: "You cannot tell whether they are working the territory." },
          { flag: "No sales or inventory reporting obligation.", consequence: "You are blind to depletions and to what is sitting in the warehouse." },
        ],
      },
      {
        title: "Money",
        items: [
          { flag: "Open-ended rights to deduct for spoils, damages or promotions.", consequence: "Your net price is decided after the fact by someone else." },
          { flag: "Free fills and samples with no cap or schedule.", consequence: "Launch costs run past anything you modelled." },
          { flag: "Unlimited right to return product.", consequence: "Revenue you recognised comes back." },
          { flag: "Promotional commitments from you with no matching commitment from them.", consequence: "You fund activity that may not happen." },
        ],
      },
      {
        title: "Getting out",
        items: [
          { flag: "No termination for convenience, or only they may terminate.", consequence: "The exit is one-directional." },
          { flag: "Long cure periods that reset on each breach.", consequence: "Termination becomes practically unreachable." },
          { flag: "You must repurchase all inventory at their cost, including aged or damaged stock.", consequence: "Leaving carries a bill you cannot size in advance." },
          { flag: "Change-of-control or brand-sale provisions giving them consent rights or a payout.", consequence: "A distributor gains leverage over your acquisition." },
          { flag: "Automatic multi-year renewal.", consequence: "You renew by forgetting." },
          { flag: "Assignment permitted for them but not for you.", consequence: "Your counterparty can change without your say." },
        ],
      },
    ],
  },

  {
    id: "three-pl",
    docId: "three-pl-agreement",
    title: "3PL Agreement — Red Flag Checklist",
    icon: "Warehouse",
    accent: "emerald",
    intro:
      "3PL fee schedules are where CPG margin quietly disappears. Model the fully loaded per-order cost against your real basket before signing.",
    sections: [
      {
        title: "Fees",
        items: [
          { flag: "A fee schedule that can be changed unilaterally.", consequence: "Your fulfilment cost is not actually a known number." },
          { flag: "Minimum monthly fees you have not compared to your slowest month.", consequence: "You pay for capacity you do not use in the off-season." },
          { flag: "Separate charges for receiving, storage, pick, pack, carton, order and EDI with no worked example.", consequence: "Nobody can predict the real cost per order until the first invoice." },
          { flag: "Storage billed per pallet position with no definition of how positions are counted.", consequence: "Partial pallets bill as full ones." },
          { flag: "Accessorial or 'special project' charges with no rate card.", consequence: "Routine work gets billed as exceptions." },
        ],
      },
      {
        title: "Your inventory",
        items: [
          { flag: "No stated inventory accuracy standard, or one with no measurement base.", consequence: "'99.5% accurate' means nothing until you know whether it counts units, cases or SKUs." },
          { flag: "A shrinkage allowance you have not converted into cases and dollars.", consequence: "An acceptable-sounding percentage can be a large number of lost cases." },
          { flag: "No liability for damage caused by their handling, or liability capped at a token amount.", consequence: "Damaged goods become your loss." },
          { flag: "No lot-level tracking or FEFO rotation.", consequence: "A 3PL built for non-perishables will fail a recall drill and ship short-dated product." },
          { flag: "No temperature commitments where the product needs them.", consequence: "A summer heat excursion ruins inventory and nobody is responsible." },
          { flag: "No cycle-count or reconciliation cadence.", consequence: "Discrepancies surface only when you run out." },
        ],
      },
      {
        title: "Getting out",
        items: [
          { flag: "A lien or right to hold your inventory against disputed charges.", consequence: "A billing dispute becomes a stock-out." },
          { flag: "Long notice periods to terminate, or termination fees.", consequence: "Leaving a bad 3PL is expensive and slow." },
          { flag: "No stated process or cost for removing your inventory.", consequence: "Your exit is priced after you announce it." },
          { flag: "No obligation to provide your data on exit.", consequence: "You lose lot history and order records you may need." },
        ],
      },
    ],
  },
];

export const RED_FLAG_BY_ID = Object.fromEntries(RED_FLAG_CHECKLISTS.map((c) => [c.id, c]));
export const RED_FLAG_BY_DOC_ID = Object.fromEntries(RED_FLAG_CHECKLISTS.map((c) => [c.docId, c]));
